import React, { useCallback } from "react";
import { DefaultButton, Dropdown, IDropdown, IDropdownOption, getTheme, mergeStyles } from "@fluentui/react";
import Header from "./Header";
import Progress from "./Progress";
import { BuildingPicker, IBuilding } from "./BuildingPicker";
import { v4 as uuidv4 } from "uuid";
import { useEffect, useState } from "react";
import HintMessage, { EHintMessageType } from "./HintMessage";

/* global require, Office, fetch, console, btoa, process */

const CUSTOM_PROP_KEY = "PortegoUniqueGuidId";
const DEFAULT_FETCH_HEADERS = {
  Accept: "application/json",
  "Content-Type": "application/json",
  Authorization: "Basic " + btoa(`${process.env.AUTH_USERNAME}:${process.env.AUTH_PASSWORD}`),
};
const API_URL = process.env.PORTEGO_URL + "/api/OutlookAddin/buildingappointmentsandtasks";
const theme = getTheme();

interface AppProps {
  title: string;
  isOfficeInitialized: boolean;
}

type AppointmentModel = {
  buildingIds: number[];
  idType: number;
  extId: string;
  from: Date;
  email: string;
};

const appWrapperClass = mergeStyles({
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  overflowWrap: "anywhere",
});

const defaultWidthClass = mergeStyles({
  minWidth: 250,
  maxWidth: "min(500px, 100vw)",
});

const mainContainerClass = mergeStyles(
  "ms-welcome__main",
  {
    // To give the seperate form rows some space
    gap: 10,
    alignSelf: "center",
    boxSizing: "border-box",
  },
  defaultWidthClass
);

const appointmentTypeDropdownClass = mergeStyles(defaultWidthClass, {
  width: "100%",
  marginBottom: 5,
});

const defaultHintMessage = {
  message: "Um eine Verknüpfung des Termins mit PORTEGO herzustellen, muss das Formular vollständig ausgefüllt werden!",
  type: EHintMessageType.Info,
};

export default function App({ title, isOfficeInitialized }: AppProps) {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasUserBeenWarned, setHasUserBeenWarned] = useState<boolean>(false);
  const [customProps, setCustomProps] = useState<Office.CustomProperties>();
  const [portegoGuid, setPortegoGuid] = useState<string>(uuidv4());
  const [appointmentTypes, setAppointmentTypes] = useState<IDropdownOption[]>([]);
  const [selectedBuildings, setSelectedBuildings] = useState<IBuilding[]>([]);
  const [selectedIdAppointmentType, setSelectedIdAppointmentType] = useState<number>();
  const [hintMessage, setHintMessage] = useState<{ message: string; type: EHintMessageType }>(defaultHintMessage);
  const dropdownRef = React.createRef<IDropdown>();

  const loadInitialData = useCallback(() => {
    Office.context.mailbox.item!.loadCustomPropertiesAsync((asyncResult) => {
      const localCustomProps = asyncResult.value;
      setCustomProps(localCustomProps);

      const existingGuid = localCustomProps.get(CUSTOM_PROP_KEY);
      if (existingGuid === undefined) {
        setIsLoading(false);
        return;
      }

      // We have an existing Appointment - load Portego data
      setPortegoGuid(existingGuid);
      const url = new URL(`${API_URL}/Appointment/Get`);
      url.search = new URLSearchParams({ extId: existingGuid }).toString();
      fetch(url, {
        method: "GET",
        headers: DEFAULT_FETCH_HEADERS,
      })
        .then((response) => response.json())
        .then((appointment) => {
          setSelectedIdAppointmentType(appointment.idType);
          setSelectedBuildings(appointment.buildings.map(mapToBuilding));
          setIsLoading(false);
        })
        .catch((err) => console.error(err));
    });
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    fetch(`${API_URL}/AppointmentType/GetAll`, {
      method: "GET",
      headers: DEFAULT_FETCH_HEADERS,
    })
      .then((response) => response.json())
      .then((appointmentTypes) => setAppointmentTypes(appointmentTypes.map((t: any) => ({ key: t.id, text: t.label }))))
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    if (hasUserBeenWarned) {
      setHasUserBeenWarned(false);
    }

    saveAppointment();
  }, [selectedIdAppointmentType, selectedBuildings]);

  function onAppointmentTypeDropdownChanged(_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption<any>) {
    setSelectedIdAppointmentType(option?.key as number);
  }

  function onBuildingSelectionChanged(items?: IBuilding[]) {
    setSelectedBuildings(items!);
  }

  async function searchBuildings(filterText: string | undefined, selectedBuildings: IBuilding[]): Promise<IBuilding[]> {
    let queryParams = new URLSearchParams();
    if (typeof filterText !== "undefined") {
      queryParams.append("value", filterText);
    }
    let buildings: IBuilding[] = await fetch(`${API_URL}/Building/Search?${queryParams}`, {
      method: "GET",
      headers: DEFAULT_FETCH_HEADERS,
    })
      .then((response) => response.json())
      .catch((e) => {
        throw new Error(e);
      });
    return buildings.filter((b) => !selectedBuildings.some((t) => t.id === b.id)).map(mapToBuilding);
  }

  function mapToBuilding(b: any): IBuilding {
    return {
      id: b.id,
      address: b.address,
      number: b.number,
      developerAcronym: b.developerAcronym,
      unitsCount: b.unitsCount,
    };
  }

  function saveAppointment() {
    if (isLoading) {
      if (selectedIdAppointmentType && selectedBuildings?.length > 0) {
        setHintMessage({
          message: "Der Termin wurde erfolgreich mit Portego verknüpft!",
          type: EHintMessageType.Success,
        });
      }
      return;
    }

    if (
      (selectedIdAppointmentType === undefined || selectedIdAppointmentType === null) &&
      (!selectedBuildings || selectedBuildings.length === 0)
    ) {
      return;
    }

    if (!isAppointmentValid()) {
      setHintMessage({
        message: "Das Formular wurde unvollständig ausgefüllt. Der Termin wird nicht mit PORTEGO verknüpft!",
        type: EHintMessageType.Error,
      });
      return;
    }

    setHintMessage({
      message: "Der Termin wird an Portego übertragen...",
      type: EHintMessageType.Info,
    });
    const existingGuid = customProps!.get(CUSTOM_PROP_KEY);
    if (existingGuid === undefined) {
      customProps!.set(CUSTOM_PROP_KEY, portegoGuid);
      customProps!.saveAsync((asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error(asyncResult.error.message);
          setHintMessage({ message: "Es ist ein unerwarteter Fehler aufgetreten", type: EHintMessageType.Error });
        } else {
          savePortegoAppointment();
        }
      });
      return;
    }

    savePortegoAppointment();
  }

  function savePortegoAppointment(): void {
    Office.context.mailbox.item!.saveAsync(() => {});
    (Office.context.mailbox.item as Office.AppointmentCompose).start.getAsync((asyncResult) => {
      let appointment: AppointmentModel = {
        email: Office.context.mailbox.userProfile.emailAddress,
        from: asyncResult.value,
        extId: portegoGuid,
        idType: selectedIdAppointmentType!,
        buildingIds: selectedBuildings.map((b) => b.id as number),
      };
      fetch(`${API_URL}/Appointment/Save`, {
        body: JSON.stringify(appointment),
        method: "POST",
        headers: DEFAULT_FETCH_HEADERS,
      })
        .then((response) => response.json())
        .then(() =>
          setHintMessage({
            message: "Der Termin wurde erfolgreich mit Portego verknüpft!",
            type: EHintMessageType.Success,
          })
        )
        .catch((err) => console.error(err));
    });
  }

  function isAppointmentValid(): boolean {
    if (selectedIdAppointmentType === undefined || selectedIdAppointmentType === null) {
      return false;
    }
    if (!selectedBuildings || selectedBuildings.length === 0) {
      return false;
    }

    return true;
  }

  function unlinkAppointmentClicked(): void {
    if (!hasUserBeenWarned) {
      setHintMessage({
        message: "Sind Sie sicher, dass Sie die Verknüpfung des Termins mit PORTEGO aufheben möchten?",
        type: EHintMessageType.Error,
      });
      setHasUserBeenWarned(true);
      return;
    }

    setHasUserBeenWarned(false);
    setSelectedIdAppointmentType(null);
    setSelectedBuildings([]);
    customProps!.remove(CUSTOM_PROP_KEY);
    customProps!.saveAsync((asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        console.error(asyncResult.error.message);
        setHintMessage({ message: "Es ist ein unerwarteter Fehler aufgetreten", type: EHintMessageType.Error });
      } else {
        setHintMessage(defaultHintMessage);
      }
    });
  }

  // <--- JSX --->
  if (!isOfficeInitialized) {
    return (
      <Progress
        title={title}
        logo={require("./../../../assets/Portego_Logo.svg")}
        message="Office is not initialized"
      />
    );
  }

  return (
    <div className={appWrapperClass}>
      <Header logo={require("./../../../assets/Portego_Logo.svg")} title={title} message="Welcome" />
      <main className={mainContainerClass}>
        <div className={appointmentTypeDropdownClass}>
          <label htmlFor={"appointment-type-dropdown"}>Terminarten:</label>
          <Dropdown
            styles={{
              title: {
                borderColor:
                  selectedIdAppointmentType !== undefined && selectedIdAppointmentType !== null
                    ? theme.palette.greenLight
                    : undefined,
              },
            }}
            componentRef={dropdownRef}
            options={appointmentTypes}
            calloutProps={{ doNotLayer: true }}
            aria-sort="ascending"
            selectedKey={selectedIdAppointmentType}
            errorMessage={!selectedIdAppointmentType ? "Sie müssen eine Terminart auswählen" : undefined}
            onChange={onAppointmentTypeDropdownChanged}
          />
        </div>
        <BuildingPicker
          onResolveSuggestions={searchBuildings}
          onChange={onBuildingSelectionChanged}
          selectedItems={selectedBuildings}
        />
        <HintMessage message={hintMessage.message} type={hintMessage.type} />
        {isAppointmentValid() && (
          <DefaultButton
            onClick={unlinkAppointmentClicked}
            styles={{ textContainer: { color: hasUserBeenWarned ? "red" : "auto" } }}
          >
            {hasUserBeenWarned ? "Ja, Verknüpfung aufheben" : "Verknüpfung aufheben"}
          </DefaultButton>
        )}
      </main>
      {/* Uncomment the following block to add some debug output: */}
      {/* <pre className="ms-font-l">
        {JSON.stringify(
          {
            portegoGuid: portegoGuid,
          },
          null,
          2
        )}
      </pre> */}
    </div>
  );
}
